import type { DriveStep } from "driver.js";

export interface HelpTour {
  key: string;
  title: string;
  description: string;
  targetPath: string;
  icon: string;
  steps: DriveStep[];
  superAdminOnly?: boolean;
}

export function getHelpTours(isSuperAdmin: boolean): HelpTour[] {
  const tours: HelpTour[] = [
    {
      key: "dashboard",
      title: "Dashboard",
      description: "Découvrez les statistiques et indicateurs clés de votre application.",
      targetPath: "/dashboard",
      icon: "LayoutDashboard",
      steps: [
        {
          element: '[data-tour="dashboard-title"]',
          popover: {
            title: "Tableau de bord",
            description: "Cette page donne une vue d'ensemble de l'activité de votre application : nombre d'utilisateurs, taux de complétion, etc.",
          },
        },
        {
          element: '[data-tour="dashboard-kpis"]',
          popover: {
            title: "Indicateurs clés (KPI)",
            description: "Chaque carte affiche une métrique importante : utilisateurs inscrits, actifs récemment, activités complétées, taux de complétion des parcours et de l'onboarding.",
          },
        },
      ],
    },
    {
      key: "paths",
      title: "Parcours",
      description: "Apprenez à créer, publier, versionner et gérer les parcours.",
      targetPath: "/paths",
      icon: "Route",
      steps: [
        {
          element: '[data-tour="paths-title"]',
          popover: {
            title: "Liste des parcours",
            description: "Cette page liste tous les parcours de l'application. Un parcours contient des activités ordonnées que les utilisateurs réalisent.",
          },
        },
        {
          element: '[data-tour="paths-create"]',
          popover: {
            title: "Créer un parcours",
            description: "Cliquez ici pour créer un nouveau parcours. Vous pourrez ensuite y ajouter des activités, configurer son type (parcours, onboarding, météo) et le publier.",
          },
        },
        {
          element: '[data-tour="paths-toolbar"]',
          popover: {
            title: "Barre d'outils",
            description: "Recherchez, filtrez par statut (brouillon/publié) ou par type, triez, et changez le mode d'affichage (compact / détaillé).",
          },
        },
        {
          element: '[data-tour="paths-list"]',
          popover: {
            title: "Liste des parcours",
            description: "Chaque carte représente un parcours avec son titre, type, statut et version. Cliquez dessus pour l'éditer. Le menu « ⋮ » donne accès aux actions : publier, nouvelle version, supprimer, initialiser pour tous.",
          },
        },
        {
          popover: {
            title: "Versioning des parcours",
            description: "Un parcours publié est immuable. Pour le modifier, créez une « nouvelle version » qui sera un brouillon éditable. Seul le super admin peut repasser une version publiée en brouillon.",
          },
        },
        {
          popover: {
            title: "Initialiser pour tous",
            description: "L'action « Initialiser pour tous les utilisateurs » (disponible sur les parcours de type 'Parcours' publiés) ajoute ce parcours en fin de liste pour tous les utilisateurs ayant terminé leur onboarding.",
          },
        },
        {
          popover: {
            title: "Points initiaux",
            description: "Chaque parcours de type 'Parcours' peut avoir des points initiaux, configurables dans l'éditeur. Ces points permettent de personnaliser l'ordre de présentation via l'onboarding.",
          },
        },
      ],
    },
    {
      key: "activities",
      title: "Activités",
      description: "Comprenez comment créer et gérer les activités, cartes et groupes de cartes.",
      targetPath: "/activities",
      icon: "Activity",
      steps: [
        {
          element: '[data-tour="activities-title"]',
          popover: {
            title: "Liste des activités",
            description: "Cette page liste toutes les activités créées. Une activité est composée d'étapes (cartes ou groupes de cartes) reliées par des transitions.",
          },
        },
        {
          element: '[data-tour="activities-create"]',
          popover: {
            title: "Créer une activité",
            description: "Créez une nouvelle activité. L'éditeur d'activité vous permettra d'ajouter des cartes, des groupes de cartes, et de les relier avec des transitions conditionnelles.",
          },
        },
        {
          element: '[data-tour="activities-toolbar"]',
          popover: {
            title: "Filtres et tri",
            description: "Recherchez par titre, filtrez par statut (brouillon/publié), triez par date, et changez le mode d'affichage.",
          },
        },
        {
          popover: {
            title: "Structure d'une activité",
            description: "Une activité contient des étapes (steps) qui peuvent être :\n• Une carte simple (ex: texte, question, vidéo)\n• Un groupe de cartes (plusieurs cartes présentées ensemble)\n\nLes étapes sont reliées par des transitions, qui peuvent être conditionnelles.",
          },
        },
        {
          popover: {
            title: "Versioning des activités",
            description: "Comme les parcours, les activités publiées sont immuables. Créez une nouvelle version pour les modifier. Le super admin peut repasser une version en brouillon.",
          },
        },
      ],
    },
    {
      key: "users",
      title: "Utilisateurs",
      description: "Gérez les utilisateurs : export, parcours, anonymisation, testeurs.",
      targetPath: "/users",
      icon: "Users",
      steps: [
        {
          element: '[data-tour="users-title"]',
          popover: {
            title: "Gestion des utilisateurs",
            description: "Cette page liste tous les utilisateurs de l'application (hors administrateurs).",
          },
        },
        {
          element: '[data-tour="users-export"]',
          popover: {
            title: "Exporter les réponses",
            description: "Exportez toutes les réponses éligibles au format CSV anonymisé (export global). Seuls les utilisateurs de 18 ans ou plus ayant accepté les CGU/PC sont inclus ; seules les réponses à cartes interactives apparaissent dans le fichier.",
          },
        },
        {
          element: '[data-tour="users-search"]',
          popover: {
            title: "Recherche",
            description: "Recherchez un utilisateur par nom, prénom ou email.",
          },
        },
        {
          element: '[data-tour="users-table"]',
          popover: {
            title: "Liste des utilisateurs",
            description: "Le tableau affiche l'email, la date d'inscription, le dernier accès, et une colonne Données (✓ ou ✗) indiquant si l'utilisateur a des réponses ou de la météo en base. L'éligibilité à l'export (18+, CGU à jour) est signalée par un badge à côté de l'email. Les testeurs sont signalés par un badge orange.",
          },
        },
        {
          popover: {
            title: "Actions disponibles",
            description: "Pour chaque utilisateur, les actions disponibles sont :\n• 🧪 Marquer/démarquer comme testeur\n• 🗺️ Gérer ses parcours (ordre, initialisation)\n• 🗑️ Supprimer le compte\n• 🔒 Anonymiser (irréversible)\n\nL'export CSV global (bouton en haut) utilise des pseudonymes et exclut les données sensibles.",
          },
        },
        {
          popover: {
            title: "Parcours de l'utilisateur",
            description: "En cliquant sur l'icône parcours (🗺️), vous pouvez voir et réorganiser l'ordre des parcours de l'utilisateur par drag & drop. Les nouveaux parcours publiés non encore initialisés seront automatiquement ajoutés.",
          },
        },
        {
          popover: {
            title: "Anonymisation",
            description: "L'anonymisation est irréversible : elle remplace toutes les données personnelles par des valeurs anonymes, tout en conservant les réponses aux activités à des fins statistiques.",
          },
        },
      ],
    },
    {
      key: "path-editor",
      title: "Éditeur de parcours",
      description: "Découvrez comment modifier un parcours : ajouter des activités, configurer les paramètres.",
      targetPath: "/paths",
      icon: "Pencil",
      steps: [
        {
          element: '[data-tour="path-editor-title"]',
          popover: {
            title: "Éditeur de parcours",
            description: "L'éditeur de parcours s'ouvre quand vous cliquez sur un parcours. Il se compose de deux zones : la liste des activités à gauche, et les paramètres à droite.",
          },
        },
        {
          element: '[data-tour="path-editor-activities"]',
          popover: {
            title: "Activités du parcours",
            description: "La colonne de gauche liste les activités du parcours dans l'ordre. Vous pouvez les réorganiser par drag & drop, en ajouter de nouvelles, ou en supprimer.",
          },
        },
        {
          element: '[data-tour="path-editor-params"]',
          popover: {
            title: "Paramètres",
            description: "L'onglet « Paramètres » à droite permet de configurer :\n• Le titre du parcours\n• Le type (parcours, onboarding, météo)\n• Le thème visuel\n• Les points initiaux (pour la personnalisation de l'ordre)",
          },
        },
        {
          element: '[data-tour="path-editor-actions"]',
          popover: {
            title: "Publication",
            description: "Les actions en haut permettent de sauvegarder ou publier le parcours. Le bouton « Publier » rend le parcours et toutes ses activités immuables. Une fois publié, il faudra créer une nouvelle version pour le modifier.",
          },
        },
      ],
    },
    {
      key: "activity-editor",
      title: "Éditeur d'activité",
      description: "Apprenez à construire une activité avec le graphe de transitions.",
      targetPath: "/activities",
      icon: "GitBranch",
      steps: [
        {
          element: '[data-tour="activity-editor-canvas"]',
          popover: {
            title: "Éditeur d'activité",
            description: "L'éditeur d'activité utilise un graphe visuel. Chaque nœud est une étape (carte ou groupe de cartes), et les flèches représentent les transitions entre étapes.",
          },
        },
        {
          element: '[data-tour="activity-editor-canvas"]',
          popover: {
            title: "Types de cartes",
            description: "Les cartes disponibles dépendent des templates configurés. Les types courants sont :\n• Texte informatif\n• Question à choix unique (single_choice)\n• Question à choix multiples\n• Vidéo, image, etc.",
          },
        },
        {
          element: '[data-tour="activity-editor-canvas"]',
          popover: {
            title: "Groupes de cartes",
            description: "Un groupe de cartes combine plusieurs cartes en une seule étape. L'utilisateur les voit les unes après les autres dans le groupe. Utile pour les questionnaires ou séquences de contenu.",
          },
        },
        {
          element: '[data-tour="activity-editor-canvas"]',
          popover: {
            title: "Transitions",
            description: "Les transitions relient les étapes entre elles. Elles peuvent être :\n• Simples : passage direct à l'étape suivante\n• Conditionnelles : dépendent de la réponse de l'utilisateur (ex: choix A → étape X, choix B → étape Y)",
          },
        },
        {
          element: '[data-tour="activity-editor-duration"]',
          popover: {
            title: "Durée de l'activité",
            description: "Par défaut, la durée est calculée automatiquement (somme des durées de chaque carte). Cliquez sur l'icône cadenas 🔒 pour forcer une durée manuellement. En mode forcé, utilisez les boutons +/- pour ajuster la valeur.",
          },
        },
        {
          element: '[data-tour="activity-editor-sidebar"]',
          popover: {
            title: "Panneau de configuration",
            description: "Sélectionnez un nœud dans le graphe pour ouvrir le panneau de configuration à droite. Vous pourrez modifier le contenu de la carte, ses options, et les transitions sortantes.",
          },
        },
      ],
    },
  ];

  if (isSuperAdmin) {
    tours.push({
      key: "settings",
      title: "Paramètres",
      description: "Configurez l'application : thèmes, templates, admins, import/export.",
      targetPath: "/settings",
      icon: "Settings",
      superAdminOnly: true,
      steps: [
        {
          element: '[data-tour="settings-title"]',
          popover: {
            title: "Paramètres de l'application",
            description: "Cette section est réservée aux administrateurs et super administrateurs. Elle centralise la configuration de l'application.",
          },
        },
        {
          element: '[data-tour="settings-tabs"]',
          popover: {
            title: "Onglets de configuration",
            description: "Les onglets disponibles sont :\n• Thèmes : couleurs et illustrations des parcours\n• Thématiques d'habitudes : catégories pour les habitudes\n• Templates de cartes : types de cartes disponibles\n• Réglages : paramètres globaux de l'application\n• Gestion des admins : inviter, modifier, supprimer des admins\n• Import / Export : sauvegarde et restauration des données",
          },
        },
        {
          popover: {
            title: "Import / Export",
            description: "La section Import/Export permet de :\n• Exporter une sauvegarde JSON (contenu, parcours, paramètres, admins — sans données utilisateurs application)\n• Importer une sauvegarde pour restaurer ou migrer les données\n• Gérer les conflits et la migration des médias",
          },
        },
      ],
    });
  }

  return tours;
}
